import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
    useGetProformaCustomersQuery,
    useGetProformaInvoiceDetailQuery,
    useUpsertProformaInvoiceMutation,
    useLazyGetProformaLaborDropdownQuery,
    useLazyGetProformaPartListQuery
} from '../../../api/endpoints/proformaInvoice';
import { useGetPreAccountingKdvQuery } from '../../../api/endpoints/preAccounting';
import { useLazyGetPackageByIdQuery, useGetPackagesQuery } from '../../../api/endpoints/packageDefinitionsApi';
import { toastService } from '../../../utils/toastService';
import ProformaPartDialog from './components/ProformaPartDialog';
import ProformaLaborDialog from './components/ProformaLaborDialog';
import ProformaOtherCostDialog from './components/ProformaOtherCostDialog';
import ProformaItemTable from './components/ProformaItemTable';

const withDiscountDefaults = (item) => ({
    ...item,
    basePrice: item.basePrice ?? item.price ?? 0,
    discountRate: item.discountRate ?? 0,
    discountAmount: item.discountAmount ?? 0
});

const applyDiscount = (basePrice, discountRate, discountAmount) => {
    const afterRate = Number(basePrice ?? 0) * (1 - (Number(discountRate ?? 0) / 100));
    return Math.max(0, Math.round((afterRate - Number(discountAmount ?? 0)) * 100) / 100);
};

const ProformaInvoiceDetailPage = () => {
    const { id: paramId } = useParams();
    const navigate = useNavigate();
    const { th, getCurrentLanguageId } = useLanguage();
    const isEditMode = !!paramId;

    const [form, setForm] = useState({
        manualEntry: false,
        customerId: null,
        customerName: '',
        phone: '',
        chassisNo: '',
        licensePlate: '',
        km: null,
        netPrice: 0,
        chassisError: ''
    });

    const [lists, setLists] = useState({
        partList: [],
        laborList: [],
        otherCostList: []
    });

    const [ui, setUi] = useState({
        activeTabIndex: 0,
        partDialogVisible: false,
        laborDialogVisible: false,
        otherCostDialogVisible: false,
        editingItem: null,
        editingIndex: null,
        customerSearchText: '',
        dropdownFilterValue: '',
        selectedPackageId: null,
        packageLoading: false
    });

    // Toplu indirim state'leri (per-tab)
    const [bulkDiscount, setBulkDiscount] = useState({
        partList:      { rate: 0, amount: 0 },
        laborList:     { rate: 0, amount: 0 },
        otherCostList: { rate: 0, amount: 0 }
    });


    const dropdownDebounceRef = useRef(null);
    const detailLoadedRef = useRef(false);
    const nextUid = useRef(1);

    const setFormField = useCallback((field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const setUiField = useCallback((field, value) => {
        setUi(prev => ({ ...prev, [field]: value }));
    }, []);

    const setListField = useCallback((field, value) => {
        setLists(prev => ({ ...prev, [field]: value }));
    }, []);

    const { data: customers = [], isFetching: fetchingCustomers } = useGetProformaCustomersQuery(
        { searchText: ui.customerSearchText, rowCount: 50 },
        { skip: form.manualEntry }
    );

    const { data: detailResp } = useGetProformaInvoiceDetailQuery(
        { proformaId: paramId },
        { skip: !isEditMode }
    );

    const [upsert, { isLoading: isSaving }] = useUpsertProformaInvoiceMutation();
    const { data: kdvData } = useGetPreAccountingKdvQuery();

    // Paket listesi
    const { data: packagesResp } = useGetPackagesQuery({ pageSize: 200, pageNumber: 1 });
    const packageOptions = useMemo(() => {
        const rows = packagesResp?.rows ?? [];
        return rows.map(p => ({ label: p.packageName, value: p.id }));
    }, [packagesResp]);

    // Lazy hooks for package loading
    const [triggerGetPackageById] = useLazyGetPackageByIdQuery();
    const [triggerGetProformaPartList] = useLazyGetProformaPartListQuery();
    const [triggerGetLaborDropdown] = useLazyGetProformaLaborDropdownQuery();

    const customerOptions = useMemo(
        () =>
            customers.map(c => ({
                label: `${c.name || ''} ${c.surname || ''} - ${c.chassisNo || ''} - ${c.licensePlate || ''}`,
                value: c.customerId,
                raw: c
            })),
        [customers]
    );

    useEffect(() => {
        if (!isEditMode || !detailResp?.data) return;

        const d = detailResp.data;

        setForm(prev => ({
            ...prev,
            customerId: d.customerId,
            customerName: d.customerNameSurname || '',
            phone: d.customerPhone || '',
            chassisNo: d.chassisNo || '',
            licensePlate: d.licensePlate || '',
            km: d.km,
            netPrice: d.netPrice ?? 0,
            manualEntry: d.customerId === -1,
            chassisError: ''
        }));

        setLists({
            partList: (d.partList || []).map((p, i) => withDiscountDefaults({ ...p, _uid: `p-${i}` })),
            laborList: (d.laborList || []).map((p, i) => withDiscountDefaults({ ...p, _uid: `l-${i}` })),
            otherCostList: (d.otherCostList || []).map((p, i) => withDiscountDefaults({ ...p, _uid: `o-${i}` }))
        });

        detailLoadedRef.current = true;
    }, [detailResp, isEditMode]);

    const kdvRateRaw = Number(kdvData?.kdv ?? kdvData?.rate ?? kdvData ?? 0) || 0;
    const kdvRatePercent = kdvRateRaw > 1 ? kdvRateRaw : kdvRateRaw * 100;
    const kdvRateDecimal = kdvRateRaw > 1 ? kdvRateRaw / 100 : kdvRateRaw;
    const kdvMultiplier = 1 + kdvRateDecimal;

    const totalPrice = useMemo(() => {
        const allItems = [...lists.partList, ...lists.laborList, ...lists.otherCostList];
        const sum = allItems.reduce((acc, item) => {
            const quantity = Number(item.quantity ?? 0);
            const unitPrice = Number(item.price ?? 0);
            return acc + quantity * unitPrice * kdvMultiplier;
        }, 0);
        return Math.round(sum * 100) / 100;
    }, [lists.partList, lists.laborList, lists.otherCostList, kdvMultiplier]);

    useEffect(() => {
        if (detailLoadedRef.current) {
            detailLoadedRef.current = false;
            return;
        }

        setForm(prev => ({
            ...prev,
            netPrice: totalPrice
        }));
    }, [totalPrice]);

    const discountPrice = useMemo(() => {
        const diff = Number(totalPrice || 0) - Number(form.netPrice || 0);
        return Math.round(diff * 100) / 100;
    }, [totalPrice, form.netPrice]);

    const handleDropdownFilterInputChange = useCallback((val) => {
        setUi(prev => ({ ...prev, dropdownFilterValue: val }));

        if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);

        dropdownDebounceRef.current = setTimeout(() => {
            setUi(prev => ({
                ...prev,
                customerSearchText: (val ?? '').trim()
            }));
        }, 400);
    }, []);

    useEffect(() => {
        return () => {
            if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);
        };
    }, []);

    const dropdownFilterTemplate = useCallback(
        () => (
            <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
                <InputText
                    value={ui.dropdownFilterValue}
                    onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
                    className="p-dropdown-filter w-full"
                    placeholder={th('common.search') || 'Ara'}
                    autoFocus
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [ui.dropdownFilterValue, handleDropdownFilterInputChange, th]
    );

    const handleCustomerSelect = (val) => {
        if (!val) {
            setForm(prev => ({
                ...prev,
                customerId: null,
                customerName: '',
                phone: '',
                chassisNo: '',
                licensePlate: '',
                chassisError: ''
            }));
            return;
        }

        const opt = customerOptions.find(o => o.value === val);

        if (opt?.raw) {
            const c = opt.raw;

            setForm(prev => ({
                ...prev,
                customerId: c.customerId,
                customerName: `${c.name || ''} ${c.surname || ''}`,
                phone: c.phoneNumber || '',
                chassisNo: c.chassisNo || '',
                licensePlate: c.licensePlate || '',
                chassisError: !c.chassisNo ? th('proformaInvoice.chassisNotFound') : ''
            }));
        }
    };

    // Paket seçilince tabloları doldur
    const handlePackageSelect = useCallback(async (packageId) => {
        if (!packageId) return;
        setUi(prev => ({ ...prev, selectedPackageId: null, packageLoading: true }));

        try {
            const pkg = await triggerGetPackageById({ id: packageId, languageId: getCurrentLanguageId() || 2 }).unwrap();

            const newParts = await Promise.all(
                (pkg.parts || []).map(async (p) => {
                    let basePrice = 0;
                    try {
                        const items = await triggerGetProformaPartList({
                            partCode: p.partCode,
                            languageId: getCurrentLanguageId() || 2,
                            pageSize: 5
                        }).unwrap();
                        const match = (items || []).find(i => i.partCode === p.partCode) || items?.[0];
                        basePrice = match?.unitPrice ?? 0;
                    } catch {
                        // fiyat çekilemezse 0
                    }
                    const qty = p.quantity ?? 1;
                    return withDiscountDefaults({
                        _uid: `pkg-p-${nextUid.current++}`,
                        itemCode: p.partCode,
                        itemName: p.partName || '',
                        description: '',
                        quantity: qty,
                        price: basePrice,
                        totalPrice: Math.round(basePrice * qty * 100) / 100,
                        warrantyType: 1
                    });
                })
            );

            const newLabors = await Promise.all(
                (pkg.labors || []).map(async (l) => {
                    let basePrice = 0;
                    let laborTime = l.laborTime ?? 1;
                    try {
                        const items = await triggerGetLaborDropdown({
                            languageId: getCurrentLanguageId() || 2,
                            searchText: l.laborName || l.laborCode,
                            pageSize: 10
                        }).unwrap();
                        const match = (items || []).find(i => String(i.id) === String(l.laborCode)) || items?.[0];
                        if (match) {
                            laborTime = Number(match.laborTime ?? laborTime);
                            const rate = Number(match.normalWarrantyRate ?? 0);
                            basePrice = Math.round(laborTime * rate * 100) / 100;
                        }
                    } catch {
                        // rate çekilemezse 0
                    }
                    return withDiscountDefaults({
                        _uid: `pkg-l-${nextUid.current++}`,
                        itemCode: String(l.laborCode || ''),
                        itemName: l.laborName || '',
                        description: '',
                        quantity: 1,
                        laborTime,
                        price: basePrice,
                        totalPrice: basePrice,
                        warrantyType: 1
                    });
                })
            );

            setLists(prev => ({
                ...prev,
                partList: [...prev.partList, ...newParts],
                laborList: [...prev.laborList, ...newLabors]
            }));

            toastService.success(th('proformaInvoice.packageApplied'));
        } catch {
            toastService.error(th('common.error'));
        } finally {
            setUiField('packageLoading', false);
        }
    }, [triggerGetPackageById, triggerGetProformaPartList, triggerGetLaborDropdown, getCurrentLanguageId, th]);

    // Kalem bazlı indirim değişince price/totalPrice güncelle
    const handleDiscountChange = useCallback((listField, index, { discountRate, discountAmount }) => {
        setLists(prev => ({
            ...prev,
            [listField]: prev[listField].map((item, i) => {
                if (i !== index) return item;
                const newRate = discountRate ?? item.discountRate ?? 0;
                const newAmount = discountAmount ?? item.discountAmount ?? 0;
                const newPrice = applyDiscount(item.basePrice ?? item.price, newRate, newAmount);
                const qty = Number(item.quantity ?? 0);
                return {
                    ...item,
                    discountRate: newRate,
                    discountAmount: newAmount,
                    price: newPrice,
                    totalPrice: Math.round(newPrice * qty * 100) / 100
                };
            })
        }));
    }, []);

    // Toplu indirim uygula
    const applyBulkDiscount = useCallback((listField) => {
        const { rate, amount } = bulkDiscount[listField];
        setLists(prev => ({
            ...prev,
            [listField]: prev[listField].map(item => {
                const newPrice = applyDiscount(item.basePrice ?? item.price, rate, amount);
                const qty = Number(item.quantity ?? 0);
                return {
                    ...item,
                    discountRate: rate,
                    discountAmount: amount,
                    price: newPrice,
                    totalPrice: Math.round(newPrice * qty * 100) / 100
                };
            })
        }));
    }, [bulkDiscount]);

    const handleSave = async () => {
        const allItems = [
            ...lists.partList.map(({ _uid, basePrice, discountRate, discountAmount, ...rest }) => ({ ...rest, type: 1 })),
            ...lists.laborList.map(({ _uid, basePrice, discountRate, discountAmount, ...rest }) => ({ ...rest, type: 2 })),
            ...lists.otherCostList.map(({ _uid, basePrice, discountRate, discountAmount, ...rest }) => ({ ...rest, type: 3 }))
        ].map(item => ({
            id: item.id || 0,
            type: item.type || 0,
            itemCode: item.itemCode || '',
            itemName: item.itemName || '',
            description: item.description || '',
            quantity: item.quantity || 0,
            price: item.price || 0,
            totalPrice: item.totalPrice || 0,
            warrantyType: item.warrantyType || 0
        }));

        const payload = {
            id: isEditMode ? Number(paramId) : 0,
            customerId: form.manualEntry ? -1 : (form.customerId || 0),
            customerNameSurname: form.customerName,
            customerPhone: form.phone,
            chassisNo: form.chassisNo,
            licensePlate: form.licensePlate,
            km: form.km || 0,
            totalPrice,
            discountPrice,
            netPrice: form.netPrice,
            items: allItems
        };

        try {
            const result = await upsert(payload).unwrap();

            if (result?.isSuccessful) {
                toastService.success(th('common.success'));
                navigate('/proforma-invoice');
            } else {
                toastService.error(result?.errorMessage || result?.responseMessage || th('common.error'));
            }
        } catch (err) {
            const msg = err?.data?.errorMessage || err?.data?.responseMessage || th('common.error');
            toastService.error(msg);
        }
    };

    const getUid = () => `new-${nextUid.current++}`;

    const closeDialogs = () => {
        setUi(prev => ({
            ...prev,
            partDialogVisible: false,
            laborDialogVisible: false,
            otherCostDialogVisible: false,
            editingItem: null,
            editingIndex: null
        }));
    };

    const handlePartSave = (item) => {
        const newItem = withDiscountDefaults({ ...item, _uid: ui.editingIndex !== null ? undefined : getUid() });
        setLists(prev => ({
            ...prev,
            partList:
                ui.editingIndex !== null
                    ? prev.partList.map((p, i) =>
                          i === ui.editingIndex ? { ...newItem, _uid: p._uid } : p
                      )
                    : [...prev.partList, newItem]
        }));
        closeDialogs();
    };

    const handleLaborSave = (item) => {
        const newItem = withDiscountDefaults({ ...item, _uid: ui.editingIndex !== null ? undefined : getUid() });
        setLists(prev => ({
            ...prev,
            laborList:
                ui.editingIndex !== null
                    ? prev.laborList.map((p, i) =>
                          i === ui.editingIndex ? { ...newItem, _uid: p._uid } : p
                      )
                    : [...prev.laborList, newItem]
        }));
        closeDialogs();
    };

    const handleOtherCostSave = (item) => {
        const newItem = withDiscountDefaults({ ...item, _uid: ui.editingIndex !== null ? undefined : getUid() });
        setLists(prev => ({
            ...prev,
            otherCostList:
                ui.editingIndex !== null
                    ? prev.otherCostList.map((p, i) =>
                          i === ui.editingIndex ? { ...newItem, _uid: p._uid } : p
                      )
                    : [...prev.otherCostList, newItem]
        }));
        closeDialogs();
    };

    const openEdit = (row, idx, type) => {
        setUi(prev => ({
            ...prev,
            editingItem: row,
            editingIndex: idx,
            partDialogVisible: type === 1,
            laborDialogVisible: type === 2,
            otherCostDialogVisible: type === 3
        }));
    };

    const BulkDiscountBar = ({ listField }) => (
        <div className="flex align-items-center flex-wrap gap-2 mb-3 px-3 py-2 surface-100 border-round border-1 surface-border">
            <span className="font-medium text-sm text-600">
                <i className="pi pi-percentage mr-1" />
                {th('proformaInvoice.bulkDiscount')}:
            </span>
            <div className="flex align-items-center gap-1">
                <InputNumber
                    value={bulkDiscount[listField].rate}
                    onChange={(e) => setBulkDiscount(prev => ({ ...prev, [listField]: { ...prev[listField], rate: e.value ?? 0 } }))}
                    suffix="%"
                    min={0}
                    max={100}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    placeholder="0"
                    inputStyle={{ width: '70px' }}
                />
                <span className="text-500 text-sm">{th('proformaInvoice.or')}</span>
                <InputNumber
                    value={bulkDiscount[listField].amount}
                    onChange={(e) => setBulkDiscount(prev => ({ ...prev, [listField]: { ...prev[listField], amount: e.value ?? 0 } }))}
                    prefix="₺"
                    min={0}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    placeholder="0"
                    inputStyle={{ width: '85px' }}
                />
            </div>
            <Button
                label={th('proformaInvoice.apply')}
                icon="pi pi-check"
                size="small"
                severity="warning"
                outlined
                onClick={() => applyBulkDiscount(listField)}
                disabled={lists[listField].length === 0}
            />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">
                    {isEditMode ? th('proformaInvoice.editTitle') : th('proformaInvoice.createTitle')}
                </h1>
                <div className="flex gap-2">
                    <Button
                        label={th('common.cancel')}
                        icon="pi pi-arrow-left"
                        outlined
                        onClick={() => navigate('/proforma-invoice')}
                    />
                    <Button
                        label={th('common.save')}
                        icon="pi pi-check"
                        onClick={handleSave}
                        loading={isSaving}
                    />
                </div>
            </div>

            <div className="p-fluid mb-4">
                <div className="flex align-items-center gap-2 mb-3">
                    <Checkbox
                        inputId="manualEntryChk"
                        checked={form.manualEntry}
                        onChange={(e) => {
                            const checked = !!e.checked;

                            setForm(prev => ({
                                ...prev,
                                manualEntry: checked,
                                customerId: checked ? -1 : null,
                                customerName: checked ? prev.customerName : '',
                                phone: checked ? prev.phone : '',
                                chassisNo: checked ? prev.chassisNo : '',
                                licensePlate: checked ? prev.licensePlate : '',
                                chassisError: ''
                            }));

                            if (!checked) {
                                setForm(prev => ({
                                    ...prev,
                                    customerId: null,
                                    customerName: '',
                                    phone: '',
                                    chassisNo: '',
                                    licensePlate: '',
                                    chassisError: ''
                                }));
                            }
                        }}
                    />
                    <label htmlFor="manualEntryChk">{th('proformaInvoice.manualEntry')}</label>
                </div>

                {!form.manualEntry && (
                    <div className="field mb-3">
                        <label className="font-bold block mb-2">{th('proformaInvoice.customer')}</label>
                        <Dropdown
                            value={form.customerId}
                            onChange={(e) => handleCustomerSelect(e.value)}
                            options={customerOptions}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={th('proformaInvoice.selectCustomer')}
                            className="w-full"
                            filter
                            filterTemplate={dropdownFilterTemplate}
                            showClear
                            loading={fetchingCustomers}
                            emptyMessage={th('common.noRecords')}
                        />
                    </div>
                )}

                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="field mb-2">
                            <label>{th('proformaInvoice.customerNameSurname')}</label>
                            <InputText
                                value={form.customerName}
                                onChange={(e) => setFormField('customerName', e.target.value)}
                                disabled={!form.manualEntry && !!form.customerId}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field mb-2">
                            <label>{th('proformaInvoice.phone')}</label>
                            <InputText
                                value={form.phone}
                                onChange={(e) => setFormField('phone', e.target.value)}
                                disabled={!form.manualEntry && !!form.customerId}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-4">
                        <div className="field mb-2">
                            <label>{th('proformaInvoice.chassisNo')}</label>
                            <InputText
                                value={form.chassisNo}
                                onChange={(e) =>
                                    setForm(prev => ({
                                        ...prev,
                                        chassisNo: e.target.value,
                                        chassisError: ''
                                    }))
                                }
                                disabled={!form.manualEntry && !!form.customerId}
                                className={form.chassisError ? 'p-invalid' : ''}
                            />
                            {form.chassisError && <small className="p-error">{form.chassisError}</small>}
                        </div>
                    </div>

                    <div className="col-12 md:col-4">
                        <div className="field mb-2">
                            <label>{th('proformaInvoice.licensePlate')}</label>
                            <InputText
                                value={form.licensePlate}
                                onChange={(e) => setFormField('licensePlate', e.target.value)}
                                disabled={!form.manualEntry && !!form.customerId}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-4">
                        <div className="field mb-2">
                            <label>{th('proformaInvoice.km')}</label>
                            <InputNumber
                                value={form.km}
                                onChange={(e) => setFormField('km', e.value)}
                                min={0}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Hazır Paket Seç */}
            <div className="mb-3 p-3 surface-50 border-round border-1 surface-border">
                <div className="flex align-items-center gap-3 flex-wrap">
                    <span className="font-semibold text-sm">
                        <i className="pi pi-box mr-2 text-primary" />
                        {th('proformaInvoice.selectPackageLabel')}
                    </span>
                    <Dropdown
                        value={ui.selectedPackageId}
                        options={packageOptions}
                        onChange={(e) => {
                            setUiField('selectedPackageId', e.value);
                            if (e.value) handlePackageSelect(e.value);
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder={th('proformaInvoice.selectPackage')}
                        filter
                        showClear
                        loading={ui.packageLoading}
                        emptyMessage={th('common.noRecords')}
                        style={{ minWidth: '260px' }}
                    />
                    {ui.packageLoading && <span className="text-sm text-500"><i className="pi pi-spin pi-spinner mr-1" />{th('proformaInvoice.packageLoading')}</span>}
                </div>
            </div>

            <Divider />

            <div className="surface-50 border-round p-3 mb-4">
                <div className="flex flex-wrap justify-content-center align-items-end gap-4">
                    <div className="field mb-0" style={{ minWidth: 140 }}>
                        <label className="block text-sm font-medium mb-1">
                            {th('proformaInvoice.kdvRate') || 'KDV Oranı'}
                        </label>
                        <InputNumber
                            value={kdvRatePercent}
                            suffix=" %"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            disabled
                            className="w-full"
                        />
                    </div>

                    <div className="field mb-0" style={{ minWidth: 160 }}>
                        <label className="block text-sm font-medium mb-1">
                            {th('proformaInvoice.totalPrice') || 'Toplam Tutar'}
                        </label>
                        <InputNumber
                            value={totalPrice}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            disabled
                            className="w-full"
                        />
                    </div>

                    <div className="field mb-0" style={{ minWidth: 160 }}>
                        <label className="block text-sm font-medium mb-1">
                            {th('proformaInvoice.netPrice') || 'Net Tutar'}
                        </label>
                        <InputNumber
                            value={form.netPrice}
                            onChange={(e) => setFormField('netPrice', e.value || 0)}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            className="w-full"
                        />
                    </div>

                    <div className="field mb-0" style={{ minWidth: 160 }}>
                        <label className="block text-sm font-medium mb-1">
                            {th('proformaInvoice.discountPrice') || 'İndirim Tutarı'}
                        </label>
                        <InputNumber
                            value={discountPrice}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            disabled
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            <TabView
                activeIndex={ui.activeTabIndex}
                onTabChange={(e) => setUiField('activeTabIndex', e.index)}
                className="w-full"
            >
                <TabPanel
                    header={
                        <span>
                            <i className="pi pi-cog mr-2" />
                            {th('proformaInvoice.partTab')}
                            {lists.partList.length > 0 && (
                                <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-blue-100 text-blue-800">
                                    {lists.partList.length}
                                </span>
                            )}
                        </span>
                    }
                >
                    <BulkDiscountBar listField="partList" />

                    <div className="flex justify-content-end mb-2">
                        <Button
                            label={th('proformaInvoice.addPart')}
                            icon="pi pi-plus"
                            severity="success"
                            size="small"
                            onClick={() =>
                                setUi(prev => ({
                                    ...prev,
                                    editingItem: null,
                                    editingIndex: null,
                                    partDialogVisible: true
                                }))
                            }
                        />
                    </div>

                    <ProformaItemTable
                        items={lists.partList}
                        onEdit={(r, i) => openEdit(r, i, 1)}
                        onDelete={(i) =>
                            setListField('partList', lists.partList.filter((_, idx) => idx !== i))
                        }
                        onDiscountChange={(i, d) => handleDiscountChange('partList', i, d)}
                        type={1}
                    />
                </TabPanel>

                <TabPanel
                    header={
                        <span>
                            <i className="pi pi-users mr-2" />
                            {th('proformaInvoice.laborTab')}
                            {lists.laborList.length > 0 && (
                                <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-green-100 text-green-800">
                                    {lists.laborList.length}
                                </span>
                            )}
                        </span>
                    }
                >
                    <BulkDiscountBar listField="laborList" />

                    <div className="flex justify-content-end mb-2">
                        <Button
                            label={th('proformaInvoice.addLabor')}
                            icon="pi pi-plus"
                            severity="success"
                            size="small"
                            onClick={() =>
                                setUi(prev => ({
                                    ...prev,
                                    editingItem: null,
                                    editingIndex: null,
                                    laborDialogVisible: true
                                }))
                            }
                        />
                    </div>

                    <ProformaItemTable
                        items={lists.laborList}
                        onEdit={(r, i) => openEdit(r, i, 2)}
                        onDelete={(i) =>
                            setListField('laborList', lists.laborList.filter((_, idx) => idx !== i))
                        }
                        onDiscountChange={(i, d) => handleDiscountChange('laborList', i, d)}
                        type={2}
                    />
                </TabPanel>

                <TabPanel
                    header={
                        <span>
                            <i className="pi pi-wallet mr-2" />
                            {th('proformaInvoice.otherCostTab')}
                            {lists.otherCostList.length > 0 && (
                                <span className="ml-2 px-2 py-1 border-round text-xs font-semibold bg-purple-100 text-purple-800">
                                    {lists.otherCostList.length}
                                </span>
                            )}
                        </span>
                    }
                >
                    <BulkDiscountBar listField="otherCostList" />

                    <div className="flex justify-content-end mb-2">
                        <Button
                            label={th('proformaInvoice.addOtherCost')}
                            icon="pi pi-plus"
                            severity="success"
                            size="small"
                            onClick={() =>
                                setUi(prev => ({
                                    ...prev,
                                    editingItem: null,
                                    editingIndex: null,
                                    otherCostDialogVisible: true
                                }))
                            }
                        />
                    </div>

                    <ProformaItemTable
                        items={lists.otherCostList}
                        onEdit={(r, i) => openEdit(r, i, 3)}
                        onDelete={(i) =>
                            setListField('otherCostList', lists.otherCostList.filter((_, idx) => idx !== i))
                        }
                        onDiscountChange={(i, d) => handleDiscountChange('otherCostList', i, d)}
                        type={3}
                    />
                </TabPanel>
            </TabView>

            <ProformaPartDialog
                visible={ui.partDialogVisible}
                onHide={closeDialogs}
                onSave={handlePartSave}
                editingItem={ui.editingItem}
            />

            <ProformaLaborDialog
                visible={ui.laborDialogVisible}
                onHide={closeDialogs}
                onSave={handleLaborSave}
                editingItem={ui.editingItem}
                chassisNo={form.chassisNo}
            />

            <ProformaOtherCostDialog
                visible={ui.otherCostDialogVisible}
                onHide={closeDialogs}
                onSave={handleOtherCostSave}
                editingItem={ui.editingItem}
            />
        </div>
    );
};

export default ProformaInvoiceDetailPage;
